import type { WishlistClient } from '#src/wishlists/http/wishlist_client.ts';
import type { GetWishlistErrorResponse, GetWishlistResponse } from '@packages/contracts/wishlist';
import { WishlistErrorCodes } from '@packages/contracts/wishlist';
import { Duration } from '@packages/primitives/duration';
import { exists } from '@packages/primitives/exists';
import { useQuery } from '@tanstack/react-query';

export interface WishlistParticipantScope {
  groupId: string;
  participantId: string;
}

export interface UseGetOwnWishlistProps extends WishlistParticipantScope {
  enabled?: boolean;
}

export function getWishlistQueryKey({ groupId, participantId }: WishlistParticipantScope) {
  return ['wishlist', groupId, participantId] as const;
}

export function useGetOwnWishlist(
  wishlistClient: WishlistClient,
  { groupId, participantId, enabled = true }: UseGetOwnWishlistProps,
) {
  const query = useQuery<GetWishlistResponse, Error>({
    queryKey: getWishlistQueryKey({ groupId, participantId }),
    queryFn: () => wishlistClient.getOwnWishlist({ groupId, participantId }),
    staleTime: Duration.ofMinutes(2),
    gcTime: Duration.ofMinutes(10),
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  });

  const { data: result, error: httpError } = query;

  return {
    isPending: query.isPending,
    isFetching: query.isFetching,
    refetch: query.refetch,
    result,
    httpError,
    items: result?.success ? result.items : [],
    hasError: exists(httpError) || (exists(result) && !result.success),
    displayErrorMessage: getDisplayErrorMessage(result, httpError),
  };
}

const GENERIC_ERROR_MESSAGE = 'We could not load your wishlist. Please try again.';

const ERROR_MESSAGES: Partial<Record<GetWishlistErrorResponse['code'], string>> = {
  [WishlistErrorCodes.NOT_FOUND]: 'We could not find your wishlist. Please refresh and try again.',
};

function getDisplayErrorMessage(
  result: GetWishlistResponse | undefined,
  unexpectedError: Error | null,
) {
  if (unexpectedError) {
    return GENERIC_ERROR_MESSAGE;
  }

  if (result && !result.success) {
    return ERROR_MESSAGES[result.code] ?? GENERIC_ERROR_MESSAGE;
  }
}
